/**
 * Navigation utility functions for consistent link generation across the app
 */

/**
 * Check if an analysis type is a rapid review
 */
export function isRapidAnalysis(type: string): boolean {
  const normalizedType = type.toLowerCase();
  return normalizedType === 'rapid' || normalizedType === 'rapid_review';
}

/**
 * Resolve the batch id an analysis belongs to, if any. Chunked batch runs
 * write `batch_id` on the analysis doc; older docs carry
 * `batch_rapid_review_id`; some callers pass a camelCase `batchId`.
 */
export function resolveAnalysisBatchId(
  analysis:
    | {
        batchId?: string | null;
        batch_id?: string | null;
        batch_rapid_review_id?: string | null;
      }
    | null
    | undefined,
): string | null {
  if (!analysis) return null;
  return (
    analysis.batchId || analysis.batch_id || analysis.batch_rapid_review_id || null
  );
}

/**
 * Build a batch-viewer route, optionally focusing a specific video via the
 * viewer's `?pv=` deep-link param.
 */
export function getBatchViewerLink(
  batchId: string,
  videoId?: string | null,
): string {
  const base = `/use-cases/rapid-review/batch-view/${batchId}`;
  return videoId ? `${base}?pv=${encodeURIComponent(videoId)}` : base;
}

/**
 * Generate the correct analysis link based on analysis type and ID
 *
 * Analyses with a known batch id land in the batch viewer (focused on this
 * video). Analyses without a known batch id stay on `/events/...`, which is
 * wrapped by `LegacyAnalysisRedirect`: the guard resolves the batch id from
 * the analysis doc and forwards batch analyses to the batch viewer.
 * Livestreams retain their live viewer; retired non-batch analysis links
 * return to the analyses index.
 *
 * @param videoId - The video ID
 * @param analysis - Object containing id, analysisType and (when known) a batch id
 * @returns The correct route path
 */
export function getAnalysisLink(
  videoId: string,
  analysis: {
    id: string;
    analysisType: string;
    batchId?: string | null;
    batch_id?: string | null;
    batch_rapid_review_id?: string | null;
  },
): string {
  const batchId = resolveAnalysisBatchId(analysis);
  if (batchId) {
    return getBatchViewerLink(batchId, videoId);
  }
  // Legacy format for analyses stored on the video document itself
  if (!analysis.id || analysis.id === 'root') {
    return `/events/${videoId}`;
  }
  // Unknown batch membership must pass through LegacyAnalysisRedirect so a
  // batch_id present on the analysis document can still be resolved.
  return `/events/${videoId}/${analysis.id}`;
}

/**
 * Viewer link for a video without a specific analysis id (legacy "root"
 * analyses stored on the video document itself, or "open this video's
 * analysis view" actions). Emits `/events/${videoId}`, which
 * `LegacyAnalysisRedirect` resolves: livestream sessions render the legacy
 * live viewer, batch analyses open Batch Viewer, and retired non-batch links
 * return to the analyses index.
 */
export function getVideoAnalysesLink(videoId: string): string {
  return getAnalysisLink(videoId, { id: "root", analysisType: "" });
}

/**
 * Generate analysis link with timestamp query parameter
 *
 * @param videoId - The video ID
 * @param analysis - Object containing id and analysisType
 * @param timestamp - The timestamp to jump to
 * @returns The route path with timestamp
 */
export function getAnalysisLinkWithTimestamp(
  videoId: string,
  analysis: { id: string; analysisType: string },
  timestamp: string | number,
): string {
  const baseUrl = getAnalysisLink(videoId, analysis);
  return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${timestamp}`;
}
